import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowLeft, MapPin, Truck, User, Clock, CheckCircle, XCircle, Download, FileText, Plus, AlertCircle } from 'lucide-react';

const ESTADO_LABELS = { nuevo:'Nuevo', en_atencion:'En Atención', resuelto:'Resuelto', cerrado:'Cerrado', falsa_alarma:'Falsa Alarma' };

export default function EventoDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accion, setAccion] = useState('');
  const [descripcionAccion, setDescripcionAccion] = useState('');
  const [showCierre, setShowCierre] = useState(false);
  const [estadoCierre, setEstadoCierre] = useState('cerrado');
  const [resumenCierre, setResumenCierre] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchEvento = async () => {
    try {
      const res = await api.get(`/eventos/${id}`);
      setEvento(res.data.data);
    } catch (e) { toast.error('Error al cargar el evento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvento(); }, [id]);

  const handleAtender = async () => {
    try {
      await api.post(`/eventos/${id}/atender`);
      toast.success('Evento tomado para atención');
      fetchEvento();
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); }
  };

  const handleAgregarAccion = async (e) => {
    e.preventDefault();
    if (!descripcionAccion.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/eventos/${id}/accion`, { accion: accion || 'ACCION', descripcion: descripcionAccion });
      toast.success('Acción registrada');
      setAccion('');
      setDescripcionAccion('');
      fetchEvento();
    } catch (e) { toast.error('Error al registrar acción'); }
    finally { setSubmitting(false); }
  };

  const handleCerrar = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/eventos/${id}/cerrar`, { estado_final: estadoCierre, resumen_cierre: resumenCierre });
      toast.success('Evento cerrado');
      setShowCierre(false);
      fetchEvento();
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); }
    finally { setSubmitting(false); }
  };

  const handleDescargarFicha = () => {
    api.get(`/exportar/ficha/${id}`, { responseType: 'blob' }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ficha_${evento?.folio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(() => toast.error('Error al generar ficha'));
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;
  if (!evento) return <div className="loading-screen">Evento no encontrado</div>;

  const isCerrado = ['cerrado', 'resuelto', 'falsa_alarma'].includes(evento.estado);
  const isNuevo = evento.estado === 'nuevo';

  const dotClass = (accion) => {
    if (['CIERRE','RESOLUCION'].includes(accion)) return 'cierre';
    if (['INICIO_ATENCION','WEBHOOK_RECIBIDO'].includes(accion)) return 'warning';
    if (accion === 'CREACION') return 'system';
    return '';
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <button className="btn-icon" onClick={() => navigate('/eventos')}><ArrowLeft size={16}/></button>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div className="page-title" style={{ fontSize:18 }}>{evento.folio}</div>
              <span className={`badge badge-${evento.prioridad}`}>{evento.prioridad}</span>
              <span className={`badge badge-${evento.estado}`}>{ESTADO_LABELS[evento.estado]}</span>
            </div>
            <div className="page-subtitle">{evento.tipoEvento?.nombre}</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {isNuevo && <button className="btn btn-warning" onClick={handleAtender}><AlertCircle size={14}/> Tomar Evento</button>}
          {!isCerrado && <button className="btn btn-success" onClick={() => setShowCierre(true)}><CheckCircle size={14}/> Cerrar Evento</button>}
          <button className="btn btn-secondary" onClick={handleDescargarFicha}><FileText size={14}/> Ficha PDF</button>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>
        {/* Info del evento */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Datos generales */}
          <div className="card">
            <div className="card-header"><span className="card-title">Información del Evento</span></div>
            <div className="card-body">
              <div className="grid-2" style={{ gap: 12 }}>
                <div>
                  <div className="form-label">Tipo de Evento</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    {evento.tipoEvento?.color && <span style={{ width:10, height:10, borderRadius:'50%', background:evento.tipoEvento.color }}/>}
                    <span>{evento.tipoEvento?.nombre || 'N/A'}</span>
                  </div>
                </div>
                <div>
                  <div className="form-label">Origen</div>
                  <span className="tag">{evento.origen?.toUpperCase()}</span>
                </div>
                <div>
                  <div className="form-label">Fecha de Alerta</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                    {evento.fecha_alerta ? format(new Date(evento.fecha_alerta), "dd/MM/yyyy HH:mm:ss", { locale: es }) : '—'}
                  </div>
                </div>
                <div>
                  <div className="form-label">Fecha de Atención</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                    {evento.fecha_atencion ? format(new Date(evento.fecha_atencion), "dd/MM/yyyy HH:mm:ss", { locale: es }) : <span className="text-muted">Sin atender</span>}
                  </div>
                </div>
                {evento.fecha_cierre && (
                  <div>
                    <div className="form-label">Fecha de Cierre</div>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>
                      {format(new Date(evento.fecha_cierre), "dd/MM/yyyy HH:mm:ss", { locale: es })}
                    </div>
                  </div>
                )}
              </div>
              {evento.descripcion && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)' }}>
                  <div className="form-label">Descripción</div>
                  <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.5 }}>{evento.descripcion}</div>
                </div>
              )}
            </div>
          </div>

          {/* Vehículo */}
          <div className="card">
            <div className="card-header"><span className="card-title"><Truck size={13} style={{marginRight:6}}/>Vehículo</span></div>
            <div className="card-body">
              <div className="grid-2" style={{ gap:12 }}>
                <div>
                  <div className="form-label">Placas</div>
                  <span className="font-display" style={{ fontSize:16, fontWeight:700, color:'var(--accent)' }}>{evento.vehiculo?.placas || 'N/A'}</span>
                </div>
                <div>
                  <div className="form-label">No. Económico</div>
                  <span>{evento.vehiculo?.numero_economico || 'N/A'}</span>
                </div>
                <div>
                  <div className="form-label">Tipo</div>
                  <span>{evento.vehiculo?.tipoVehiculo?.nombre || 'N/A'}</span>
                </div>
                <div>
                  <div className="form-label">Cliente</div>
                  <span>{evento.cliente?.nombre || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Localización */}
          {(evento.latitud || evento.ubicacion_descripcion) && (
            <div className="card">
              <div className="card-header"><span className="card-title"><MapPin size={13} style={{marginRight:6}}/>Localización</span></div>
              <div className="card-body">
                {evento.ubicacion_descripcion && <div style={{ marginBottom:8, fontSize:13 }}>{evento.ubicacion_descripcion}</div>}
                {evento.latitud && (
                  <div style={{ display:'flex', gap:16 }}>
                    <div><div className="form-label">Latitud</div><span className="font-mono" style={{fontSize:12}}>{evento.latitud}</span></div>
                    <div><div className="form-label">Longitud</div><span className="font-mono" style={{fontSize:12}}>{evento.longitud}</span></div>
                    {evento.velocidad && <div><div className="form-label">Velocidad</div><span className="font-mono" style={{fontSize:12}}>{evento.velocidad} km/h</span></div>}
                  </div>
                )}
                {evento.latitud && (
                  <a href={`https://maps.google.com/?q=${evento.latitud},${evento.longitud}`} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ marginTop:10 }}>
                    <MapPin size={12}/> Ver en Google Maps
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Monitorista */}
          <div className="card">
            <div className="card-header"><span className="card-title"><User size={13} style={{marginRight:6}}/>Monitorista</span></div>
            <div className="card-body">
              {evento.monitorista ? (
                <div>
                  <div style={{ fontSize:15, fontWeight:600 }}>{evento.monitorista.nombre} {evento.monitorista.apellido}</div>
                  <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>{evento.monitorista.email}</div>
                </div>
              ) : (
                <span className="text-muted">Sin monitorista asignado</span>
              )}
            </div>
          </div>

          {/* Resumen cierre */}
          {evento.resumen_cierre && (
            <div className="card">
              <div className="card-header"><span className="card-title">Resumen de Cierre</span></div>
              <div className="card-body" style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6 }}>{evento.resumen_cierre}</div>
            </div>
          )}
        </div>

        {/* Bitácora + Nueva acción */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Nueva acción */}
          {!isCerrado && (
            <div className="card">
              <div className="card-header"><span className="card-title"><Plus size={13} style={{marginRight:6}}/>Registrar Acción</span></div>
              <div className="card-body">
                <form onSubmit={handleAgregarAccion}>
                  <div className="form-group">
                    <label className="form-label">Tipo de Acción</label>
                    <select className="form-control" value={accion} onChange={e => setAccion(e.target.value)}>
                      <option value="ACCION">Acción General</option>
                      <option value="CONTACTO_CONDUCTOR">Contacto con Conductor</option>
                      <option value="CONTACTO_CLIENTE">Contacto con Cliente</option>
                      <option value="VERIFICACION">Verificación</option>
                      <option value="COORDINACION">Coordinación con Autoridades</option>
                      <option value="SEGUIMIENTO">Seguimiento</option>
                      <option value="NOTA">Nota Informativa</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Descripción</label>
                    <textarea className="form-control" value={descripcionAccion} onChange={e => setDescripcionAccion(e.target.value)} placeholder="Describe la acción realizada..." required rows={3} />
                  </div>
                  <button className="btn btn-primary btn-sm" type="submit" disabled={submitting}>
                    {submitting ? <span className="spinner" style={{width:12,height:12}}/> : <Plus size={12}/>} Agregar
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Bitácora */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Bitácora</span>
              <span className="tag">{evento.bitacora?.length || 0} entradas</span>
            </div>
            <div className="card-body">
              {(!evento.bitacora || evento.bitacora.length === 0) && (
                <div className="text-muted" style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>Sin entradas en bitácora</div>
              )}
              <div className="timeline">
                {evento.bitacora?.map((entrada, i) => (
                  <div key={entrada.id} className="timeline-item">
                    <div className="timeline-line">
                      <div className={`timeline-dot ${dotClass(entrada.accion)}`}/>
                      {i < evento.bitacora.length - 1 && <div className="timeline-connector"/>}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-action">{entrada.accion}</div>
                      <div className="timeline-time">
                        {entrada.createdAt ? format(new Date(entrada.createdAt), "dd/MM/yy HH:mm:ss", { locale: es }) : '—'}
                      </div>
                      <div className="timeline-desc">{entrada.descripcion}</div>
                      <div className="timeline-user">
                        Por: {entrada.usuario ? `${entrada.usuario.nombre} ${entrada.usuario.apellido}` : 'Sistema'}
                        {entrada.estado_anterior && entrada.estado_nuevo && entrada.estado_anterior !== entrada.estado_nuevo && (
                          <span style={{ marginLeft:8, color:'var(--accent)', fontSize:10 }}>
                            {ESTADO_LABELS[entrada.estado_anterior]} → {ESTADO_LABELS[entrada.estado_nuevo]}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Cierre */}
      {showCierre && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">Cerrar Evento</span>
              <button className="btn-icon" onClick={() => setShowCierre(false)}><XCircle size={16}/></button>
            </div>
            <form onSubmit={handleCerrar}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Estado Final</label>
                  <select className="form-control" value={estadoCierre} onChange={e => setEstadoCierre(e.target.value)}>
                    <option value="resuelto">Resuelto</option>
                    <option value="cerrado">Cerrado</option>
                    <option value="falsa_alarma">Falsa Alarma</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Resumen de Cierre</label>
                  <textarea className="form-control" value={resumenCierre} onChange={e => setResumenCierre(e.target.value)} placeholder="Describe cómo se resolvió el evento..." rows={4} required />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCierre(false)}>Cancelar</button>
                <button type="submit" className="btn btn-success" disabled={submitting}>
                  {submitting ? <span className="spinner" style={{width:14,height:14}}/> : <CheckCircle size={14}/>} Confirmar Cierre
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
